import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import CheckboxBlock from '../../../../../../../features/components/Form/CheckboxBlock'
import Field from '../../../../../../../components/Form/Field'
import MessageBlock from '../../../../../../../features/components/Form/MessageBlock'
import ProgressButton from '../../../../../../../components/Form/ProgressButton'
import ErrorMessage from '../../../../../../../components/Form/ErrorMessage'
import SocialAuthChecker from '../../../../../../../features/components/SocialAuthChecker'

import { getAdAccount, clearSpecificPlatformSingleAdAccount } from '../../../../../../../modules/actions/adAccounts'

import { FACEBOOK_PLATFORM } from '../../../../../../../constants/selectLists/platformList'

import useStyles from './styles'

const PlatformCheckboxBlock = ({
  formik,
  title,
  className,
  checkboxFieldName,
  adAccountFieldName,
  adAccountDataFieldName,
  platform,
  isLoadingSelector,
  dataSelector,
  wasLoadedSelector,
  errorSelector,
  showAcceptTosLink = false
}) => {
  const classes = useStyles()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const adAccountIsLoading = useSelector(isLoadingSelector)
  const adAccountData = useSelector(dataSelector)
  const adAccountWasLoaded = useSelector(wasLoadedSelector)
  const adAccountError = useSelector(errorSelector)

  const { values, setFieldValue, setFieldTouched, errors, touched } = formik

  const { [adAccountFieldName]: adAccountFieldValue, [adAccountDataFieldName]: adAccountDataValue } = values

  const isAdAccountDataPopulated = !!Object.keys(adAccountDataValue).length

  // separate state to fix some issues with UI states for ProgressButton
  const [adAccountWasTested, setAdAccountWasTested] = useState(isAdAccountDataPopulated)

  const {
    name,
    currency,
    timezone_name: timezoneName,
    tos_accepted: tosAccepted,
    funding_source: fundingSource,
    external_id: adAccountId
  } = adAccountDataValue

  const showError = errors[adAccountDataFieldName] && touched[adAccountDataFieldName]

  const memoizedAdAccountError = useMemo(
    () => (!adAccountWasTested ? adAccountError : {}),
    [adAccountError, adAccountWasTested]
  )

  const onAdAccountValueChange = useCallback(() => {
    // clear existing data when user change ad account that was previously tested
    if (adAccountWasTested) {
      dispatch(clearSpecificPlatformSingleAdAccount(platform))
      setAdAccountWasTested(false)
      setFieldValue(adAccountDataFieldName, {})
      setFieldTouched(adAccountDataFieldName, false)
    }
  }, [dispatch, adAccountWasTested, platform, setFieldValue, setFieldTouched, adAccountDataFieldName])

  const testAccessHandler = useCallback(() => {
    if (values[adAccountFieldName]) {
      dispatch(getAdAccount(adAccountFieldValue, platform))
    }
  }, [dispatch, values, adAccountFieldName, adAccountFieldValue, platform])

  const getAdAccountAccessContent = () => {
    if (isAdAccountDataPopulated) {
      return (
        <MessageBlock title="Ad account found" state="success" color="success" className={classes.messageBlock}>
          <span className={classes.successMessageRow}>
            {t('Name')}: <span className={classes.successMessageValue}>{name}</span>
          </span>
          <span className={classes.successMessageRow}>
            {t('Currency')}: <span className={classes.successMessageValue}>{currency}</span>
          </span>
          <span className={classes.successMessageRow}>
            {t('Timezone')}: <span className={classes.successMessageValue}>{timezoneName}</span>
          </span>
          {platform === FACEBOOK_PLATFORM && (
            <>
              <span className={classes.successMessageRow}>
                {t('TOS accepted:')}{' '}
                <span
                  className={classnames({
                    [classes.successMessageGreenValue]: tosAccepted,
                    [classes.successMessageRedValue]: !tosAccepted
                  })}
                >
                  {tosAccepted ? (
                    t('True')
                  ) : showAcceptTosLink ? (
                    <span>
                      {t('False')}{' '}
                      <a
                        rel="noreferrer nofollow"
                        target="_blank"
                        href={`https://business.facebook.com/ads/manage/customaudiences/tos.php?act=${adAccountId}`}
                      >
                        {t('Click here to accept')}
                      </a>
                    </span>
                  ) : (
                    t('False')
                  )}
                </span>
              </span>
              <span className={classes.successMessageRow}>
                {t('Funding Source ')}:{' '}
                <span
                  className={classnames({
                    [classes.successMessageGreenValue]: fundingSource,
                    [classes.successMessageRedValue]: !fundingSource
                  })}
                >
                  {fundingSource ? t('Added') : t('Not added')}
                </span>
              </span>
            </>
          )}
        </MessageBlock>
      )
    } else if (adAccountError) {
      return (
        <MessageBlock title="Ad account not found" borderColor="warning" className={classes.messageBlock}>
          {t('Something went wrong. Please try another ad account')}
        </MessageBlock>
      )
    }

    return (
      <ProgressButton
        type="button"
        className={classes.testButton}
        onClick={testAccessHandler}
        isFormLoading={adAccountIsLoading}
        formError={memoizedAdAccountError}
        successSubmit={!adAccountWasTested && adAccountWasLoaded}
      >
        {t('Test Access')}
      </ProgressButton>
    )
  }

  useEffect(() => {
    // when ad account is loaded, set its data to formik state
    if (adAccountWasLoaded && !isAdAccountDataPopulated) {
      setFieldValue(adAccountDataFieldName, adAccountData)
    }
  }, [adAccountData, adAccountDataFieldName, adAccountWasLoaded, isAdAccountDataPopulated, setFieldValue])

  useEffect(() => {
    if (!adAccountWasTested && (adAccountWasLoaded || adAccountError)) {
      setAdAccountWasTested(true)
    }
  }, [dispatch, adAccountWasTested, adAccountError, adAccountWasLoaded])

  return (
    <CheckboxBlock
      id={`checkbox_${title}_ad_account`}
      title={t(title)}
      className={classnames(classes.checkboxBlock, className)}
      checked={values[checkboxFieldName]}
      onCheck={() => {
        setFieldValue(checkboxFieldName, !values[checkboxFieldName])
      }}
    >
      <SocialAuthChecker
        platform={platform}
        authFailureText={`Please authenticate with ${title} to be able add an ad account.`}
      >
        <Field
          placeholder="Ad account id"
          onValueChange={onAdAccountValueChange}
          formik={formik}
          name={adAccountFieldName}
        />
        {getAdAccountAccessContent()}
      </SocialAuthChecker>
      {showError && <ErrorMessage error={errors[adAccountDataFieldName]} />}
    </CheckboxBlock>
  )
}

PlatformCheckboxBlock.propTypes = {
  formik: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  className: PropTypes.string,
  checkboxFieldName: PropTypes.string.isRequired,
  adAccountFieldName: PropTypes.string.isRequired,
  adAccountDataFieldName: PropTypes.string.isRequired,
  platform: PropTypes.string.isRequired,
  isLoadingSelector: PropTypes.func.isRequired,
  dataSelector: PropTypes.func.isRequired,
  wasLoadedSelector: PropTypes.func.isRequired,
  errorSelector: PropTypes.func.isRequired
}

export default PlatformCheckboxBlock
